import React from 'react';
import { Card, ListGroup, Spinner, Alert, Table } from 'react-bootstrap';
import { getContrastColor, COLOR_SUCCESS, COLOR_WARNING, COLOR_PRIMARY, COLOR_DANGER, COLOR_SECONDARY } from '../../../shared/colorUtils';
import { IconClock, IconUser, IconEdit } from '@tabler/icons-react';
import { api } from '../../../shared/api';
import { useRealtimeEvents } from '../../../shared/useRealtimeEvents';
import { formatDateTime, formatDate } from '../../../shared/utils/dateUtils';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../../shared/CurrencyContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import PageSection from '../../../components/layout/PageSection';
import EmptyState from '../../../components/feedback/EmptyState';

const RentalSettingsHistory = ({ rental }) => {
  const { t } = useTranslation();
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();

  const { data: historyData = [], isLoading: loading, isError } = useQuery({
    queryKey: ['rental-history', rental.id],
    queryFn: () => api.get(`rentals/api/rentals/${rental.id}/history/`),
  });
  const error = isError ? t('rentals.settings.history.loadError') : null;

  useRealtimeEvents((e) => {
    if (e.model === 'rentals.rental' && String(e.pk) === String(rental.id)) {
      queryClient.invalidateQueries({ queryKey: ['rental-history', rental.id] });
    }
  });

  // Helper function to format the field name for display
  const formatFieldName = (fieldName) => {
    const fieldNameMap = {
      'customer': t('rentals.settings.customer.customer'),
      'date_from': t('rentals.settings.general.from'),
      'date_to': t('rentals.settings.general.to'),
      'price': t('rentals.settings.pricing.subtotal'),
      'price_vat': t('rentals.settings.pricing.totalWithTax'),
      'rental_date': t('rentals.settings.general.rentalDate'),
      'rental_status': t('rentals.settings.general.status'),
      'rental_reason': t('rentals.settings.general.rentalReason'),
      'issued_by': t('rentals.tableHeaders.issuedBy'),
      'note': t('rentals.settings.general.notes'),
      'discount_percentage': t('rentals.settings.pricing.discount')
    };

    return fieldNameMap[fieldName] || fieldName;
  };

  // Helper function to format values for display
  const formatValue = (fieldName, value) => {
    if (value === null || value === undefined) return t('rentals.settings.history.notSpecified');

    // Format dates
    if (['date_from', 'date_to'].includes(fieldName)) {
      return formatDateTime(value);
    }

    if (fieldName === 'rental_date') {
      return formatDate(value);
    }

    // Format status
    if (fieldName === 'rental_status') {
      const statusMap = {
        'active': { color: COLOR_SUCCESS, text: t('rentals.statuses.active') },
        'inactive': { color: COLOR_WARNING, text: t('rentals.statuses.inactive') },
        'completed': { color: COLOR_PRIMARY, text: t('rentals.statuses.completed') },
        'expired': { color: COLOR_DANGER, text: t('rentals.statuses.expired') },
        'canceled': { color: COLOR_SECONDARY, text: t('rentals.statuses.canceled') }
      };

      const { color, text } = statusMap[value] || { color: '#adb5bd', text: value };
      return <span className="badge" style={{ backgroundColor: color, color: getContrastColor(color) }}>{text}</span>;
    }

    // Format prices
    if (['price', 'price_vat'].includes(fieldName)) {
      return formatCurrency(value, { decimals: 0 });
    }

    // Format discount
    if (fieldName === 'discount_percentage') {
      return `${value} %`;
    }

    return value.toString();
  };

  if (loading) {
    return (
      <PageSection title={t('rentals.settings.history.title')}>
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      </PageSection>
    );
  }

  if (error) {
    return (
      <PageSection title={t('rentals.settings.history.title')}>
        <Alert variant="danger" className="app-inline-alert mb-0">{error}</Alert>
      </PageSection>
    );
  }

  return (
    <PageSection
      title={t('rentals.settings.history.title')}
      description={t('rentals.settings.history.description', 'Přehled změn zápůjčky v čase včetně úprav cen, termínů a stavu.')}
    >
      {historyData.length === 0 ? (
        <EmptyState
          title={t('rentals.settings.history.noHistory')}
          message={t('rentals.settings.history.noHistoryMessage', 'Zatím tu není žádná zaznamenaná změna této zápůjčky.')}
        />
      ) : (
        <Table responsive hover className="table-vcenter app-table mb-0">
          <thead>
            <tr>
              <th>{t('rentals.settings.history.date')}</th>
              <th>{t('rentals.settings.history.user')}</th>
              <th>{t('rentals.settings.history.action')}</th>
              <th>{t('common.details')}</th>
            </tr>
          </thead>
          <tbody>
            {historyData.map((item, index) => (
              <tr key={index}>
                <td>
                  <IconClock size={16} className="me-2 text-muted" />
                  {formatDateTime(item.timestamp)}
                </td>
                <td>
                  <IconUser size={16} className="me-2 text-muted" />
                  {item.user_name || t('rentals.settings.history.systemAction')}
                </td>
                <td>
                  <span
                    className="badge"
                    style={{
                      backgroundColor: item.action === 'CREATE' ? COLOR_SUCCESS : COLOR_PRIMARY,
                      color: getContrastColor(item.action === 'CREATE' ? COLOR_SUCCESS : COLOR_PRIMARY)
                    }}
                  >
                    {item.action === 'CREATE'
                      ? t('rentals.settings.history.created')
                      : t('rentals.settings.history.updated')}
                  </span>
                </td>
                <td>
                  {item.changes && Object.keys(item.changes).length > 0 ? (
                    <ListGroup variant="flush">
                      {Object.entries(item.changes).map(([field, values]) => (
                        <ListGroup.Item key={field} className="px-0 py-2 border-0">
                          <div className="d-flex align-items-center">
                            <IconEdit size={16} className="me-2 text-muted" />
                            <strong>{formatFieldName(field)}: </strong>
                            <span className="ms-2 text-danger text-decoration-line-through me-2">
                              {formatValue(field, values.old)}
                            </span>
                            <span className="text-success">
                              {formatValue(field, values.new)}
                            </span>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : (
                    <span className="text-muted">{t('rentals.settings.history.noChanges')}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </PageSection>
  );
};

export default RentalSettingsHistory;
